import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Route, useHistory, useLocation, Redirect, Switch, NavLink } from 'react-router-dom';

import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import BottomNavigation from '@material-ui/core/BottomNavigation';
import BottomNavigationAction from '@material-ui/core/BottomNavigationAction';
import Hidden from '@material-ui/core/Hidden';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';

// Dialog Components
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import AccountIcon from '@material-ui/icons/AccountCircle';
import PaymentIcon from '@material-ui/icons/Payment';
import PasswordIcon from '@material-ui/icons/Https';
import SettingsIcon from '@material-ui/icons/Settings';
import MembershipIcon from '@material-ui/icons/VerifiedUser';
import LogoutIcon from '@material-ui/icons/ExitToApp';

import Snapshot from 'app/components/user/Snapshot';
import Profile from 'app/components/user/Profile';
import Associations from 'app/components/user/Associations';
import Payment from 'app/components/user/Payment';
import Security from 'app/components/user/Security';
import Settings from 'app/components/user/Settings';

import useApi from 'app/context/useApi';

import Loading from 'app/components/loaders/Loading';
import RedButton from 'app/components/buttons/RedButton';

import { divider } from 'app/styles/core';
import { containerFlex, containerFlexFixedSmall, containerFlexGrow } from 'app/styles/new-layout';

const useStyles = makeStyles(theme => ({
    container: {
        ...containerFlex(theme),
        padding: theme.spacing(3),
    },
    nav: {
        ...containerFlexFixedSmall(theme),
    },
    content: {
        ...containerFlexGrow(theme),
        paddingLeft: theme.spacing(3),

        [theme.breakpoints.down('sm')]: {
            paddingLeft: '0px',
        }
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    divider: {
        ...divider(theme)
    },
    menuItem: {
        borderRadius: theme.shape.borderRadius
    },
    bottomNavigation: {
        left: 0,
        width: '100%',
        bottom: 0,
        zIndex: 100,
        position: 'fixed',
        boxShadow: '0px 5px 10px #111111'
    },
    bottomNavigationAction: {
        minWidth: 60
    },
    bottomNavigationSpacer: theme.mixins.toolbar
}));

export default function UserProfile(props) {

    const [ user, setUser ] = useState(null);
    const [ customValues, setCustomValues ] = useState(null);
    const [ verifyLogout, setVerifyLogout ] = useState(false);
    const [ logoutLoading, setLogoutLoading ] = useState(false);

    const classes = useStyles();
    const api = useApi();

    let history = useHistory();
    let location = useLocation();

    const pathname = location.pathname;
    const urlPrefix = '/profile';

    // Get Functions ------------------------------------------

    const getUser = () => {
        api.get(urlPrefix).then(res => {
            setUser(res.user);
            setCustomValues(res.association.values)
        });
    }

    useEffect(() => {
        getUser();
    }, []);

    const handleVerifyLogout = () => {
        setVerifyLogout(true);
    }

    const handleLogoutCancel = () => {
        setVerifyLogout(false);
    }

    const handleLogout = () => {

        setLogoutLoading(true);

        const bearerToken = api.defaults.headers.common['Authorization'];
        const token = bearerToken.substr(bearerToken.indexOf(" ") + 1);

        api.delete('/oauth/token/' + token).catch(() => setLogoutLoading(false));
    };

    const handleBottomNavChange = (event, value) => {
        history.push(value);
    }

    const isSelected = tab => (tab === pathname);

    return (
        <div className={classes.container}>
            <div className={classes.nav}>
                <Snapshot urlPrefix={urlPrefix} user={user} getUser={getUser}/>
                <div className={classes.marginTop}/>
                <Hidden smDown>
                    <Divider/>
                    <MenuList>
                        <MenuItem
                            to="/profile"
                            className={classes.menuItem}
                            selected={isSelected("/profile")}
                            component={NavLink}
                            >
                            <ListItemIcon>
                                <AccountIcon />
                            </ListItemIcon>
                            <ListItemText primary="Profile" />
                        </MenuItem>
                        <MenuItem
                            to="/profile/clubs"
                            className={classes.menuItem}
                            selected={isSelected("/profile/clubs")}
                            component={NavLink}
                            >
                            <ListItemIcon>
                                <MembershipIcon />
                            </ListItemIcon>
                            <ListItemText primary="Organizations" />
                        </MenuItem>
                        <MenuItem
                            to="/profile/payment"
                            className={classes.menuItem}
                            selected={isSelected("/profile/payment")}
                            component={NavLink}
                            >
                            <ListItemIcon>
                                <PaymentIcon />
                            </ListItemIcon>
                            <ListItemText primary="Payment" />
                        </MenuItem>
                        <MenuItem
                            to="/profile/security"
                            className={classes.menuItem}
                            selected={isSelected("/profile/security")}
                            component={NavLink}
                            >
                            <ListItemIcon>
                                <PasswordIcon />
                            </ListItemIcon>
                            <ListItemText primary="Security" />
                        </MenuItem>
                        <MenuItem
                            to="/profile/settings"
                            className={classes.menuItem}
                            selected={isSelected("/profile/settings")}
                            component={NavLink}
                            >
                            <ListItemIcon>
                                <SettingsIcon />
                            </ListItemIcon>
                            <ListItemText primary="Settings" />
                        </MenuItem>
                    </MenuList>
                    <Divider/>
                    <MenuList>
                        <MenuItem dense button onClick={handleVerifyLogout}>
                            <ListItemIcon>
                                <LogoutIcon />
                            </ListItemIcon>
                            <ListItemText primary="Logout" />
                        </MenuItem>
                    </MenuList>
                </Hidden>
            </div>
            <div className={classes.content}>
                <Switch>
                    <Route path={'/profile'} exact>
                        <Profile user={user} urlPrefix={urlPrefix} getUser={getUser} customValues={customValues}/>
                    </Route>
                    <Route path={'/profile/clubs'} exact>
                        <Associations />
                    </Route>
                    <Route path={'/profile/payment'} exact>
                        <Payment urlPrefix={urlPrefix}/>
                    </Route>
                    <Route path={'/profile/security'} exact>
                        <Security urlPrefix={urlPrefix} user={user}/>
                    </Route>
                    <Route path={'/profile/settings'} exact>
                        <Settings />
                    </Route>
                    <Redirect from={"/profile/*"} to="/profile" />
                </Switch>
            </div>
            <Hidden mdUp>
                <div className={classes.bottomNavigationSpacer}/>
                <BottomNavigation
                    onChange={handleBottomNavChange}
                    value={pathname}
                    className={classes.bottomNavigation}
                    >
                    <BottomNavigationAction label="Profile" value="/profile" icon={<AccountIcon/>}
                        classes={{ root: classes.bottomNavigationAction }} />
                    <BottomNavigationAction label="Organizations" value="/profile/clubs" icon={<MembershipIcon/>}
                        classes={{ root: classes.bottomNavigationAction }} />
                    <BottomNavigationAction label="Payment" value='/profile/payment' icon={<PaymentIcon/>}
                        classes={{ root: classes.bottomNavigationAction }} />
                    <BottomNavigationAction label="Security" value='/profile/security' icon={<PasswordIcon/>}
                        classes={{ root: classes.bottomNavigationAction }} />
                    <BottomNavigationAction label="Settings" value='/profile/settings' icon={<SettingsIcon/>}
                        classes={{ root: classes.bottomNavigationAction }} />
                </BottomNavigation>
            </Hidden>
            <Dialog
                open={verifyLogout}
                onClose={handleLogoutCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                >
                <Loading isLoading={logoutLoading}/>
                <DialogTitle id="alert-dialog-title">Confirm you want to logout</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to logout? You will be directed back to the login screen.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleLogoutCancel} color="primary" autoFocus>
                        Cancel
                    </Button>
                    <RedButton onClick={handleLogout} variant="contained">
                        Logout
                    </RedButton>
                </DialogActions>
            </Dialog>
        </div>
    );
}
